import { useEffect, useState } from 'react';
import { Checkbox } from './FieldComponents';
import { ArrowDown, ArrowUp, ChevronDown, Filter, PlusCircle, RefreshCcw } from 'lucide-react';

export const AwaitingAction = ({ fetchCounts }) => {
    const [loading, setLoading] = useState(true);
    const [actions, setActions] = useState([]);
    const [selectedActions, setSelectedActions] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 100,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
    });
    const [perPageOptions] = useState([25, 50, 100, 200, 500]);

    const fetchActions = async (page = 1, perPage = pagination.per_page) => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                action: 'true',
                page: page.toString(),
                per_page: perPage.toString()
            });
            
            const response = await fetch(`https://api.casedb.co/invoice-cases.php?${params}`, {
                credentials: 'include'
            });
            const data = await response.json();
            setActions(data.data || []);
            setPagination({
                current_page: data.pagination?.current_page || page,
                per_page: data.pagination?.per_page || perPage,
                total: data.pagination?.total || 0,
                total_pages: data.pagination?.total_pages || 0,
                has_next: data.pagination?.has_next || false,
                has_prev: data.pagination?.has_prev || false
            });
        } catch (error) {
            console.error('Error fetching actions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        fetchActions(newPage, pagination.per_page);
    };

    const handlePerPageChange = (newPerPage) => {
        setPagination(prev => ({ ...prev, per_page: newPerPage }));
        fetchActions(1, newPerPage); // Reset to page 1 when changing per page
    };

    useEffect(() => {
        fetchActions();    
    }, []);

    useEffect(() => {
        if (actions.length > 0) {
            fetchCounts();
        }
    }, [actions, fetchCounts]);

    const pushToStaging = async () => {
        if (selectedActions.length === 0) return;
    
        try {
            setLoading(true);
    
            const selectedData = selectedActions.map(id => {
                const action = actions.find(a => a.id === id);
    
                const isTrust = action?.['289'] || action?.['290'] || action?.['291'] || action?.['292'];
    
                return {
                    case_id: action.case_id,
                    group_id: action.group_id,
                    payee: action?.['269'] || action?.['289'],
                    type: isTrust ? 1 : 0,
                    more_info: action?.['275'] || action?.['292'],
                    amount: action?.['270'] || action?.['290'],
                    memo: action?.['274'] || action?.['291']
                };
            });
    
            const response = await fetch('https://api.casedb.co/invoice-cases.php?push=true', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ actions: selectedData }),
            });
    
            const data = await response.json();
            if (data.success) {
                setActions(prev => prev.filter(action => !selectedActions.includes(action.id)));
                setSelectedActions([]);
            } else {
                console.error('Error pushing to staging:', data.message);
            }
        } catch (error) {
            console.error('Error pushing to staging:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const formatCurrency = (amount) => {
        if (isNaN(amount)) return '';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    };

    const handleSelectAction = (id) => {
        setSelectedActions(prev => {
            if (prev.includes(id)) {
                return prev.filter(actionId => actionId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedActions.length === filteredActions.length) {
            setSelectedActions([]);
        } else {
            setSelectedActions(filteredActions.map(action => action.id));
        }
    };

    if (loading) {
        return <div className="loading-container">Loading...</div>;
    }

    const filteredActions = actions.filter(action => {
        if (!filter) return true;
        const term = filter.toLowerCase();
        return (
            (action?.client_name || '').toLowerCase().includes(term) ||
            (action?.['274'] || action?.['291'] || '').toLowerCase().includes(term) ||
            (action?.payee || '').toLowerCase().includes(term)
        );
    });

    return (
        <div className="awaiting-action-container">
            <div className="accounting-header">
                <div className="accounting-header-left">
                    <h3>Awaiting Action {selectedActions.length > 0 && `(${selectedActions.length} selected)`}</h3>
                </div>
            
                <div className="accounting-header-right">
                    <div className="select-wrapper">
                        <select 
                            className='default-select' 
                            value={pagination.per_page} 
                            onChange={(e) => handlePerPageChange(parseInt(e.target.value))}
                        >
                            {perPageOptions.map(option => (
                                <option key={option} value={option}>{option} per page</option>
                            ))}
                        </select>
                        <ChevronDown size={16} className="select-chevron" />
                    </div>

                    <button className="action alt" onClick={() => setFilterOpen(!filterOpen)}>
                        <Filter size={16} />
                        Filter
                    </button>
                    <button className="action alt" onClick={() => fetchActions(pagination.current_page, pagination.per_page)}>
                        <RefreshCcw size={16} />
                        Refresh
                    </button>
                </div>
            </div>

            {filterOpen && (
                <div className="filter-section">
                    <div className="keystroke-filter">
                        <input 
                            type="text" 
                            value={filter} 
                            onChange={(e) => setFilter(e.target.value)} 
                            placeholder="Search by client, case, memo, or payee"
                        />
                    </div>
                    
                    <div className="filter-actions">
                        <button className="action alt" onClick={() => setFilter('')}>Reset</button>
                        <button 
                            className="action alt" 
                            onClick={() => handleSelectAll(!selectedActions.length || selectedActions.length !== filteredActions.length)}
                        >
                            {selectedActions.length === filteredActions.length ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>
                </div>
            )}
            {filteredActions.length > 0 ? (
                <>
                    <div className="invoice-table-container">
                        <table className="invoice-table subtext">
                            <thead>
                                <tr>
                                    <th className='invoice-select'>
                                        <Checkbox 
                                            value={selectedActions.length > 0 && selectedActions.length === filteredActions.length} 
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <th className="invoice-small">Client Name</th>
                                    <th className="invoice-medium">Memo</th>
                                    <th className="invoice-small">Case</th>
                                    <th className="invoice-small">Payee</th>
                                    <th className="invoice-small">Type</th>
                                    <th className="invoice-small">More Info</th>
                                    <th className="invoice-small">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredActions.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="empty-row">No actions found</td>
                                    </tr>
                                ) : (
                                    filteredActions.map((action) => {
                                        const clientNameParts = action?.client_name?.trim().split(' ') || [];
                                        const firstName = clientNameParts.shift() || '';
                                        const lastName = clientNameParts.join(' ') || '';

                                        return (
                                            <tr 
                                                key={action.id} 
                                                className={`invoice-row ${selectedActions.includes(action.id) ? 'selected-row' : ''}`}
                                            >
                                                <td className="invoice-select">
                                                    <Checkbox 
                                                        value={selectedActions.includes(action.id)} 
                                                        onChange={() => handleSelectAction(action.id)}
                                                    />
                                                </td>
                                                <td className='invoice-small' title={action?.client_name}>{action?.client_name?.length > 20 ? `${action?.client_name?.slice(0, 20)}...` : action?.client_name}</td>
                                                <td className='invoice-medium'>{action?.['274'] || action?.['291']}</td>
                                                <td className='invoice-small'>{<a href={`/case/${action.case_id}?section=${(action?.['289'] || action?.['290'] || action?.['291'] || action?.['292']) ? '26' : '25'}`} className='subtext-link fill-background-contrast'>{action.case_id}</a>}</td>
                                                <td className='invoice-small' title={action?.payee}>{action?.payee?.length > 20 ? `${action?.payee?.slice(0, 20)}...` : action?.payee}</td>
                                                <td className='invoice-small'>{(action?.['289'] || action?.['290'] || action?.['291'] || action?.['292']) ? 'Trust Accounting' : 'Expenses'}</td>
                                                <td className='invoice-small'>{action?.['275'] || action?.['292']}</td>
                                                <td className='invoice-small'>{formatCurrency(action?.['270'] || action?.['290'])}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    <div className='pagination-controls' style={{ justifyContent: 'center', marginTop: '20px' }}>
                        <button 
                            className='action alt' 
                            disabled={!pagination.has_prev}
                            onClick={() => handlePageChange(pagination.current_page - 1)}
                        >
                            Previous
                        </button>
                        <span className='pagination-info subtext'>
                            Page {pagination.current_page} of {pagination.total_pages} ({pagination.total} total actions)
                        </span>
                        <button 
                            className='action alt' 
                            disabled={!pagination.has_next}
                            onClick={() => handlePageChange(pagination.current_page + 1)}
                        >
                            Next
                        </button>
                    </div>
                </>
            ) : (
                <p>No actions awaiting your attention.</p>
            )}

            {selectedActions.length > 0 && (
                <div className='push-to-staging'>
                    <button className='action' onClick={pushToStaging}>Push to Invoice Staging</button>
                </div>
            )}
        </div>
    );
};